import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import i18next from "i18next"
import _ from 'lodash';
import { Storage } from 'aws-amplify';

// @ts-ignore
import './StellantisRequestQuoteModal.scss';
import { Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactSelectOption } from "src/types";
import { useApiClient } from "src/hooks/useApiClient";
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import * as StellantisConstant from './stellantisConstants';
import * as StellantisUtils from './stellantisUtils';
import { randomString } from "src/utils";
import { useErrorToast } from "src/hooks/useErrorToast";
import toaster from "src/utils/toaster";
import * as StellantisValidation from "./stellantisValidations";
import StellantisFormFields from "./StellantisFormFields";
import { QuoteRequestActionDto, StellantisQuoteRequestDTO } from "src/api/models/StellantisDTO";
import type { QuotationPdfTag } from "src/quotations/types";
import dayjs from "dayjs";
import { useAuth } from "src/hooks/useAuth";
import { DealerDTO, UserDTO } from "src/api";
import { dealersMap } from "src/utils/users";
import { mapServices } from "src/utils/pdfUtils";
import usePdfStore from "src/store/PdfStore";
import VehiclePdfTemplate from "../VehicleDetails/VehiclePdfTemplate";
import LoadingOverlay from "./LoadingOverlay";
import { useQuery } from "@tanstack/react-query";

type Props = {
  quoteRequest: StellantisQuoteRequestDTO;
  closeModal: () => void;
  isOpen: boolean;
}

type QuoteFile = { file: File | null, error?: string | null }
type QuotePdf = { key: string; fileName: string; fileType?: string };

const StellantisEditQuoteRequestModal: React.FC<Props> = ({ isOpen, quoteRequest, closeModal }) => {
  const { apiClient, handleApiResponse } = useApiClient();
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [externalPdf, setExternalPdf] = useState<QuoteFile>({ file: null })
  const [hmpPdf, setHmpPdf] = useState<QuoteFile>({ file: null })
  const { user } = useAuth();
  const { showError } = useErrorToast();
  const [dealerLogo, setDealerLogo] = useState<string | null>(null);
  const { setPdfDetail } = usePdfStore();
  const [isGeneratingHmpPdf, setGeneratingHmpPdf] = useState(false);
  const [isBusy, setBusy] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState<string | null>();
  const [requesterInfo, setRequesterInfo] = useState<UserDTO | null>(null);
  const canoneRef = useRef<HTMLElement | null>(null);
  const optionalsRef = useRef<HTMLElement | null>(null);

  const { data: noleggiatoreOptions } = useQuery<ReactSelectOption[]>({
    queryKey: ['fetch_noleggiatore_options'],
    queryFn: async () => {
      const response = await handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityFields());
      const picklist = response.find(item => item.name?.toLowerCase() === 'noleggiatore__c')?.picklistValues || [];
      return picklist.filter(item => item.active).map(item => _.pick(item, ['label', 'value']));
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const fetchRequesterInfo = async () => {
    if (!quoteRequest.requestedBy) {
      return;
    }

    try {
      const userInfo = await handleApiResponse(
        apiClient.userController.getRegisterSingleUser(quoteRequest.requestedBy)
      );
      userInfo && setRequesterInfo(userInfo);
    } catch (error) {
      showError(error, i18next.t('error_loading_user_data'));
    }
  }

  useEffect(() => { fetchDealerLogo() }, [quoteRequest?.dealer])

  useEffect(() => { fetchRequesterInfo() }, [quoteRequest?.requestedBy])

  const onCloseModal = () => {
    closeModal();
  }

  const uploadFilesToBucket = async (externalPdf: File | null, generatedHmpPdf: File) => {
    try {
      if (!externalPdf && !generatedHmpPdf) {
        return {}
      }

      const result: { externalPdf?: QuotePdf | null; hmpPdf?: QuotePdf | null } = { externalPdf: null, hmpPdf: null };

      if (externalPdf) {
        const ext = externalPdf.name.split('.').pop();
        const key = `quote-request/${quoteRequest.id}/${crypto.randomUUID()}.${ext}`;
        const payload = { file: externalPdf, fileName: externalPdf.name, key, fileType: ext };

        const s3Response = await Storage.put(payload.key, payload.file, {
          contentType: payload.file.type,
          bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_STELLANTIS_DOCUMENTS,
        });
        result.externalPdf = { fileName: payload.fileName, key: `public/${s3Response.key}`, fileType: payload.fileType }
      }

      if (generatedHmpPdf) {
        const ext = generatedHmpPdf.name.split('.').pop();
        const key = `quote-request/${quoteRequest.id}/${crypto.randomUUID()}.${ext}`;
        const payload = { file: generatedHmpPdf, fileName: generatedHmpPdf.name, key, fileType: ext };

        const s3Response = await Storage.put(payload.key, payload.file, {
          contentType: payload.file.type,
          bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_STELLANTIS_DOCUMENTS,
        });
        result.hmpPdf = { fileName: payload.fileName, key: `public/${s3Response.key}`, fileType: payload.fileType }
      }
      return result;
    } catch (e) {
      showError(e, i18next.t('error_uploading_file'))
      throw e;
    }
  }

  const buildPayload = async (values: any, generatedHmpPdf: File) => {
    const quotePdfs = await uploadFilesToBucket(externalPdf.file, generatedHmpPdf);

    // Vehicle specs (alimentazione/cambio/cavalli/...) and images are stored on the
    // quote at creation and flow through `values`, so no vehicle lookup is needed here.
    const payload = {
      ...values,
      ...quotePdfs,
      canone: StellantisUtils.isNilOrEmpty(values.canone) ? null : values.canone,
      autoSostitutivaGruppo: StellantisUtils.isNilOrEmpty(values.autoSostitutivaGruppo) ? null : values.autoSostitutivaGruppo,
      cognome: StellantisUtils.isNilOrEmpty(values.cognome) ? null : values.cognome,
      nome: StellantisUtils.isNilOrEmpty(values.nome) ? null : values.nome,
      ragioneSociale: StellantisUtils.isNilOrEmpty(values.ragioneSociale) ? null : values.ragioneSociale,
      tipoGomma: StellantisUtils.isNilOrEmpty(values.tipoGomma) ? null : values.tipoGomma,
      requestNote: StellantisUtils.isNilOrEmpty(values.requestNote) ? null : values.requestNote,
      optionals: values.optionals?.filter(opt => !StellantisUtils.isNilOrEmpty(opt.descrizione)) || [],
      images: values.images || quoteRequest.quote?.images || [],
    }

    return payload;
  }

  const generateHmpPdf = async (values) => {
    try {
      setBusy(true)
      setGeneratingHmpPdf(true)
      setLoaderMessage(i18next.t('generating_hmp_pdf'));
      const pdfData = await getPdfData(values);
      const idempotency = randomString(10);
      setPdfDetail({ data: { ...pdfData, idempotency } });
    } catch (error) {
      setBusy(false);
      setGeneratingHmpPdf(false);
      setLoaderMessage(null);
      showError(error, i18next.t('hmp_pdf_generation_failed'));
    }
  };

  const handleSubmit = async (values: any, generatedHmpPdf: File) => {
    try {
      setPendingUpdate(true);
      setLoaderMessage(i18next.t('exec_quote_request'));
      const payload = await buildPayload(values, generatedHmpPdf);
      await handleApiResponse(apiClient.stellantisControllerService.doQuoteRequest(quoteRequest.id, payload));
      toaster.success(i18next.t('quote_update_success'));
      closeModal?.()
    } catch (error) {
      showError(error, i18next.t('quote_update_failure'));
    } finally {
      setPendingUpdate(false);
      setBusy(false);
      setLoaderMessage(null);
    }
  }

  const fetchDealerLogo = async () => {
    try {
      const quoteDealer = quoteRequest.dealer?.toUpperCase();

      const dealers: DealerDTO[] = await handleApiResponse(
        apiClient.dealerController.getRegisterDealers()
      );

      let dealer: DealerDTO | undefined = undefined;

      for (let item of dealers) {
        let ragioneSociale = item.ragione_sociale
          .toUpperCase()
          .replaceAll('_', ' ');

        if (
          ragioneSociale === quoteDealer ||
          (dealersMap[item.ragione_sociale] !== undefined &&
            dealersMap[item.ragione_sociale] === quoteDealer)
        ) {
          dealer = item;
        }
      }

      if (dealer && dealer.logo?.length) {
        const logo = await Storage.get(dealer.logo, {
          bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_DEALER_LOGO,
          cacheControl: 'no-cache, no-store, must-revalidate',
        });

        setDealerLogo(logo);
      }
    } catch (error) {
      console.warn('Failed to fetch dealer logo', error);
    }
  }

  const getPdfData = async (quoteAction: QuoteRequestActionDto) => {
    const offerta: any = {
      data_valid: dayjs().format('YYYY-MM-DD'),
      name: quoteAction.tipoCliente === 'azienda' ? quoteAction.ragioneSociale : `${quoteAction.nome} ${quoteAction.cognome}`,
      durata: quoteAction.durata,
      anticipo: quoteAction.anticipoNoIva,
      km_totali: quoteAction.kmAnnui,
      importo_privato: quoteAction.canone,
      importo_azienda: quoteAction.canone,
      offerType: ['azienda', 'libero_professionista'].includes(quoteAction.tipoCliente) ? 'company' : 'private',
      servizi: {
        treno_di_gomme: quoteAction.gommeYN ? 'Si' : 'No',

        ...((quoteAction.gommeYN && quoteAction.tipoGomma)
          ? { tipo_gomma: quoteAction.tipoGomma?.toUpperCase() }
          : {}),

        gomme_illimitate_YN: quoteAction.gommeIllimitateYN,

        ...((!quoteAction.gommeIllimitateYN)
          ? {
            num_treno_gomme_estive: quoteAction.numTrenoGommeEstive || 0,
            num_treno_gomme_invernali: quoteAction.numTrenoGommeInvernali || 0
          }
          : {}),

        kasko: quoteAction.kaskoFranchigia ? (quoteAction.kaskoFranchigia + ' €') : quoteAction.kaskoFranchigia,
        rca: quoteAction.rcaPenaleRisarcitoria ? (quoteAction.rcaPenaleRisarcitoria + ' €') : quoteAction.rcaPenaleRisarcitoria,
        auto_sostitutiva: quoteAction.autoSostitutivaYN ? 'Si' : 'No',
        ...((quoteAction.autoSostitutivaYN && quoteAction.autoSostitutivaGruppo)
          ? { auto_sostitutiva_gruppo: quoteAction.autoSostitutivaGruppo?.toUpperCase() }
          : {}),
        i_f: quoteAction.incendioFurtoScopertoPerc ? `${quoteAction.incendioFurtoScopertoPerc} %` : quoteAction.incendioFurtoScopertoPerc,
      },
      optionals: (quoteAction.optionals || []).filter(opt => !!opt.descrizione && !opt.unquotable),
    }

    const images = (quoteAction as any).images || quoteRequest.quote?.images || [];
    let vehicleImages: any = [];

    if (images.length) {
      // Vehicle images (JATO-hosted and Motornet) are public URLs — embed directly.
      vehicleImages.push({ url: images[0] });
    } else {
      vehicleImages.push({ url: '/images/placeholder.png' });
    }

    const pdfData = {
      lista_offerte: [
        {
          id: 'customPdf',
          durata: offerta.durata,
          km_totali: offerta.km_totali,
          servizi: offerta.servizi,
          anticipo: offerta.anticipo,
          importo_azienda: offerta.importo_azienda,
          importo_privato: offerta.importo_privato,
          noleggiatore: quoteAction.noleggiatore ?? null,
        },
      ],
      pdf: {
        data_validita: offerta.data_valid,
        name: offerta.name,
        userName: requesterInfo?.given_name,
        userSurname: requesterInfo?.family_name,
        userMail: requesterInfo?.username,
        offerType: offerta.offerType,
        offerId: 'customPdf',
        dealerDetail: { logo: dealerLogo },
        services: mapServices('', offerta.servizi),
      },
      dati_veicolo: {
        marca: quoteAction.marca,
        modello: quoteAction.modello,
        allestimento: quoteAction.allestimento,
        alimentazione: quoteAction.alimentazione,
        cambio: quoteAction.cambio,
        cavalli: quoteAction.cavalli,
        cilindrata: quoteAction.cilindrata,
        kw: quoteAction.kw,
        porte: quoteAction.porte,
        posti: quoteAction.posti,
      },
      optionals: offerta.optionals.filter((el) => el.descrizione.length > 0),
      image_list: vehicleImages,
      quotation: {
        source: 'stellantis',
        vehicle: {
          makeCode: null,
          modelCode: null,
          vehicleCode: null,
          quoteRequestId: quoteRequest.id,
        },
      } as QuotationPdfTag,
    };
    return pdfData;
  };

  const onPdfCreated = async (filename: string, pdfBlob: Blob, values: any) => {
    const file = new File([pdfBlob], `${filename}-HMP-${new Date().getTime()}.pdf`);
    setHmpPdf({ file })
    setGeneratingHmpPdf(false);
    return handleSubmit(values, file);
  }

  const initialValues: any = useMemo(() => {
    const value = {
      ...StellantisConstant.makeDefaultQuoteRequestValues(),
      ..._.pick(quoteRequest.quote, StellantisConstant.quoteRequestEditFields),
      optionals: (quoteRequest.quote?.optionals || [])?.map(opt => ({ ...opt, valore: opt.valore }))
    }

    if (!value.optionals?.length) {
      value.optionals = [];
    }

    return value;
  }, [quoteRequest]);

  const hasOptionals = Boolean((quoteRequest.quote?.optionals || [])?.filter(opt => !!opt?.descrizione?.trim())?.length > 0);
  const hasNotes = Boolean(quoteRequest.quote?.requestNote?.trim());
  const hasProvvigioni = Boolean(quoteRequest.quote?.provvigioni);

  useEffect(() => {
    const ref = hasOptionals ? optionalsRef : canoneRef;
    ref.current?.scrollIntoView({
      behavior: 'smooth'
    })
  }, [])

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onCloseModal}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className='display-6'>{i18next.t("request_quote")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <fieldset>
          <Formik
            initialValues={initialValues}
            onSubmit={generateHmpPdf}
            validationSchema={StellantisValidation.updateRequestSchema}
            enableReinitialize={true}
          >
            {({ values, handleSubmit }) => {
              return (
                <Form className="p-0" onSubmit={handleSubmit}>
                  <Row>
                    <Col sm={values.tipoCliente === 'azienda' ? 6 : 4}>
                      <StellantisFormFields.TipoCliente disabled />
                    </Col>
                    {['libero_professionista', 'privato'].includes(values.tipoCliente) && (
                      <>
                        <Col sm={4}>
                          <StellantisFormFields.Nome disabled />
                        </Col>
                        <Col sm={4}>
                          <StellantisFormFields.Cognome disabled />
                        </Col>
                      </>
                    )}
                    {values.tipoCliente === 'azienda' && (
                      <Col sm={6}>
                        <StellantisFormFields.RagioneSociale disabled />
                      </Col>
                    )}
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>{i18next.t('brand')}</Form.Label>
                        <Form.Control type="text" value={values.marca || ''} disabled readOnly />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>{i18next.t('model')}</Form.Label>
                        <Form.Control type="text" value={values.modello || ''} disabled readOnly />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>{i18next.t('allestimento')}</Form.Label>
                        <Form.Control type="text" value={values.allestimento || ''} disabled readOnly />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col sm={4}>
                      <StellantisFormFields.KmAnnui disabled />
                    </Col>
                    <Col sm={4}>
                      <StellantisFormFields.Durata disabled />
                    </Col>
                    <Col sm={4}>
                      <StellantisFormFields.AnticipoNoIva disabled />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col sm={4}>
                      <StellantisFormFields.Rca disabled />
                    </Col>
                    <Col sm={4}>
                      <StellantisFormFields.KaskoFranchigia disabled />
                    </Col>
                    <Col sm={4}>
                      <StellantisFormFields.IncendioFurtoScopertoPerc disabled />
                    </Col>
                  </Row>

                  <hr />
                  <Row>
                    <Col md={6}>
                      <StellantisFormFields.GommeYN disabled />
                    </Col>
                    {values.gommeYN && (
                      <>
                        <Row md={12} className="mt-2">
                          <Col sm={4}>
                            <StellantisFormFields.TipoGomma disabled />
                          </Col>
                          <Col sm={4}>
                            <StellantisFormFields.NumTrenoGommeEstive disabled />
                          </Col>
                          <Col sm={4}>
                            <StellantisFormFields.NumTrenoGommeInvernali disabled />
                          </Col>
                        </Row>
                        <Row md={12}>
                          <StellantisFormFields.GommeIllimitate disabled />
                        </Row>
                      </>)
                    }
                  </Row>
                  <hr />
                  <Row>
                    <Col md={6}>
                      <StellantisFormFields.AutoSostitutiva disabled />
                    </Col>
                  </Row>
                  {values.autoSostitutivaYN &&
                    (<Row className="mt-2">
                      <Col md={4}>
                        <StellantisFormFields.GruppoAutoSostitutiva disabled />
                      </Col>
                    </Row>)}
                  {hasOptionals && (
                    <>
                      <hr />
                      <Row ref={optionalsRef}>
                        <StellantisFormFields.QuoteOptionals />
                      </Row>
                    </>
                  )}
                  <hr />
                  <Row>
                    <Col sm={4} ref={canoneRef}>
                      <StellantisFormFields.Canone />
                    </Col>
                    {hasProvvigioni && (
                      <Col sm={4}>
                        <StellantisFormFields.Provvigioni disabled />
                      </Col>
                    )}
                    <Col sm={4}>
                      <StellantisFormFields.Noleggiatore options={noleggiatoreOptions || []} />
                    </Col>
                  </Row>
                  {hasNotes && (
                    <>
                      <hr />
                      <Row>
                        <Col>
                          <StellantisFormFields.RequestNote disabled />
                        </Col>
                      </Row>
                    </>
                  )}
                  <hr />
                  <Row>
                    <Col>
                      <StellantisFormFields.ExternalPdf externalPdf={externalPdf} setExternalPdf={setExternalPdf} />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col md={12} className="d-flex justify-content-end">
                      <Button onClick={onCloseModal} disabled={pendingUpdate} className="me-2">
                        <SpinnerButton label={i18next.t('close')} />
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        // onClick={(e) => {
                        //   e.preventDefault()
                        //   console.log({values})
                        // }}
                        disabled={pendingUpdate || !externalPdf.file}
                      >
                        <SpinnerButton
                          busy={pendingUpdate}
                          label={i18next.t('do_quote')}
                        />
                      </Button>
                    </Col>
                  </Row>
                  {isGeneratingHmpPdf && <VehiclePdfTemplate onDone={(filename, pdfBlob) => onPdfCreated(filename, pdfBlob, values)} />}
                </Form>
              );
            }}
          </Formik>
        </fieldset>
      </Modal.Body>
      <LoadingOverlay show={isBusy} message={loaderMessage} />
    </Modal>
  )
}


export default StellantisEditQuoteRequestModal
